import React, { useEffect, useState } from "react";
import axios from "axios";
import cookie from "react-cookies";
import CurrencyFormat from "react-currency-format";
import PlacesAutocomplete from "../../components/common/PlacesAutocomplete";
import constants from "../../Constants";
import "../multi-stop-book/multi-stop-book.css";

const baseURL = constants.baseURL;
const TRACK_BASE = "https://qiktruck.co.za/track-order";

const STOP_TYPES = [
  { value: "waypoint", label: "General stop" },
  { value: "pickup", label: "Extra pickup" },
  { value: "delivery", label: "Extra drop-off" },
];

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  };
}

function locationLabel(loc, index, total) {
  if (loc.role === "pickup" || index === 0) return "Start · collection";
  if (loc.role === "delivery" || index === total - 1) return "End · final delivery";
  return `Stop ${index}`;
}

function swapPair(arr, i, j) {
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

function stopsToLocations(routeStops) {
  return (routeStops || []).map((stop, index, arr) => ({
    id: `stop-${stop.sequence}-${index}`,
    role:
      index === 0 ? "pickup" : index === arr.length - 1 ? "delivery" : "waypoint",
    address: stop.address || "",
    lat: stop.lat,
    lng: stop.lng,
    stopType: stop.stop_type || "waypoint",
    contactName: stop.contact_name || "",
    contactPhone: stop.contact_phone || "",
    accessNotes: stop.notes || "",
  }));
}

function locationsToPayload(locations) {
  return locations.map((loc, index) => ({
    sequence: index + 1,
    stop_type:
      index === 0
        ? "pickup"
        : index === locations.length - 1
          ? "delivery"
          : loc.stopType,
    address: loc.address,
    lat: loc.lat,
    lng: loc.lng,
    notes: loc.accessNotes,
    contact_name: loc.contactName,
    contact_phone: loc.contactPhone,
  }));
}

function shortAddress(address) {
  if (!address) return "";
  return address.split(",")[0];
}

function orderIdFromPath() {
  const match = window.location.pathname.match(/^\/route-set\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

export default function RouteSetEditSection() {
  const orderid = orderIdFromPath();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apierror, setApierror] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [editable, setEditable] = useState(false);
  const [editableReason, setEditableReason] = useState("");
  const [parentOrderId, setParentOrderId] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const [locations, setLocations] = useState([]);
  const [legDescriptions, setLegDescriptions] = useState([]);

  useEffect(() => {
    if (!orderid) return;
    setLoading(true);
    setApierror("");

    axios
      .get(`${baseURL}/customer-route-set`, {
        headers: authHeaders(),
        params: { orderid },
      })
      .then((res) => {
        const data = res.data || {};
        setParentOrderId(data.parentOrderId || orderid);
        setEditable(Boolean(data.editable));
        setEditableReason(data.editableReason || "");
        setTotalPrice(data.totalPrice || 0);
        setLocations(stopsToLocations(data.routeStops));
        setLegDescriptions(data.legDescriptions || []);
        setLoading(false);
      })
      .catch((err) => {
        setApierror(
          err.response?.data?.error ||
            "Could not load this route. It may not be a multi-stop booking."
        );
        setLoading(false);
      });
  }, [orderid]);

  const updateLocation = (id, patch) => {
    setLocations((prev) => prev.map((loc) => (loc.id === id ? { ...loc, ...patch } : loc)));
    setSuccessMsg("");
  };

  const moveStop = (id, direction) => {
    if (!editable) return;
    const idx = locations.findIndex((loc) => loc.id === id);
    if (idx <= 0 || idx >= locations.length - 1) return;

    const swapWith = direction === "up" ? idx - 1 : idx + 1;
    if (swapWith <= 0 || swapWith >= locations.length - 1) return;

    setLocations((prev) => swapPair(prev, idx, swapWith));
    setLegDescriptions((prev) => {
      const legA = Math.min(idx, swapWith);
      const legB = Math.max(idx, swapWith);
      return swapPair(prev, legA, legB);
    });
    setSuccessMsg("");
  };

  const updateLegDescription = (index, value) => {
    setLegDescriptions((prev) => prev.map((d, i) => (i === index ? value : d)));
    setSuccessMsg("");
  };

  const validAddresses = () =>
    locations.every((loc) => loc.lat && loc.lng) && locations.length >= 3;

  const legsComplete = () =>
    legDescriptions.length === locations.length - 1 &&
    legDescriptions.every((d) => String(d || "").trim().length > 0);

  const saveRoute = async () => {
    if (!editable) return;
    setApierror("");
    setSuccessMsg("");

    if (!validAddresses()) {
      setApierror("Every stop needs a valid address on the map.");
      return;
    }
    if (!legsComplete()) {
      setApierror("Describe what moves on each driver leg.");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(
        `${baseURL}/customer-route-set-update`,
        {
          orderid: parentOrderId || orderid,
          routeStops: locationsToPayload(locations),
          legDescriptions,
        },
        { headers: authHeaders() }
      );
      const data = res.data || {};
      setLocations(stopsToLocations(data.routeStops));
      setLegDescriptions(data.legDescriptions || legDescriptions);
      setEditable(Boolean(data.editable));
      setSuccessMsg("Route updated. Drivers will see the new stops and leg details.");
    } catch (err) {
      setApierror(err.response?.data?.error || "Could not save route changes.");
    } finally {
      setSaving(false);
    }
  };

  const legCount = Math.max(1, locations.length - 1);
  const locationCount = locations.length;

  const renderLocationFields = (loc, index) => {
    const isIntermediate = index > 0 && index < locations.length - 1;
    const readOnly = !editable;

    return (
      <div className={`msb-stop-card${loc.lat ? " msb-stop-card--filled" : ""}`}>
        <div className="msb-stop-head">
          <span
            className={`msb-stop-label${
              index === 0
                ? " msb-stop-label--pickup"
                : index === locations.length - 1
                  ? " msb-stop-label--delivery"
                  : ""
            }`}
          >
            {locationLabel(loc, index, locations.length)}
          </span>
          {isIntermediate && editable && (
            <div className="msb-stop-actions">
              {index > 1 && (
                <button
                  type="button"
                  className="msb-stop-move"
                  onClick={() => moveStop(loc.id, "up")}
                  title="Visit this stop earlier on the route"
                >
                  <i className="mdi mdi-arrow-up" />
                  Earlier
                </button>
              )}
              {index < locations.length - 2 && (
                <button
                  type="button"
                  className="msb-stop-move"
                  onClick={() => moveStop(loc.id, "down")}
                  title="Visit this stop later on the route"
                >
                  <i className="mdi mdi-arrow-down" />
                  Later
                </button>
              )}
            </div>
          )}
        </div>

        {readOnly ? (
          <div className="msb-form-input" style={{ background: "#f8f9fa" }}>
            {loc.address || "-"}
          </div>
        ) : (
          <PlacesAutocomplete
            type="text"
            className="msb-form-input"
            placeholder={
              index === 0
                ? "Where should we collect?"
                : index === locations.length - 1
                  ? "Final drop-off address"
                  : "Stop address"
            }
            defaultValue={loc.address}
            onPlaceSelected={(place) => {
              updateLocation(loc.id, {
                lat: place?.geometry?.location?.lat(),
                lng: place?.geometry?.location?.lng(),
                address: place?.formatted_address || "",
              });
            }}
          />
        )}

        {isIntermediate && editable && (
          <div className="msb-stop-type-row">
            {STOP_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                className={`msb-stop-type${loc.stopType === t.value ? " msb-stop-type--active" : ""}`}
                onClick={() => updateLocation(loc.id, { stopType: t.value })}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="msb-contact-row">
          <input
            type="text"
            className="msb-form-input"
            placeholder="Contact name at this stop"
            value={loc.contactName}
            readOnly={readOnly}
            onChange={(e) => updateLocation(loc.id, { contactName: e.target.value })}
          />
          <input
            type="tel"
            className="msb-form-input"
            placeholder="Contact phone"
            value={loc.contactPhone}
            readOnly={readOnly}
            onChange={(e) => updateLocation(loc.id, { contactPhone: e.target.value })}
          />
        </div>

        <input
          type="text"
          className="msb-form-input"
          style={{ marginTop: 8 }}
          placeholder="Access notes (gate code, unit, floor, etc.)"
          value={loc.accessNotes}
          readOnly={readOnly}
          onChange={(e) => updateLocation(loc.id, { accessNotes: e.target.value })}
        />
      </div>
    );
  };

  const renderLegCard = (legIndex) => {
    const from = locations[legIndex];
    const to = locations[legIndex + 1];

    return (
      <div className="msb-leg-card">
        <div className="msb-leg-head">
          <span className="msb-leg-badge">
            <i className="mdi mdi-truck-fast" />
            Driver leg {legIndex + 1} of {legCount}
          </span>
          <span className="msb-leg-route">
            {shortAddress(from?.address) || `Stop ${legIndex + 1}`}
            {" → "}
            {shortAddress(to?.address) || `Stop ${legIndex + 2}`}
          </span>
        </div>
        <label className="msb-form-label">What moves on this leg?</label>
        <textarea
          className="msb-form-input"
          rows={2}
          value={legDescriptions[legIndex] || ""}
          readOnly={!editable}
          onChange={(e) => updateLegDescription(legIndex, e.target.value)}
          placeholder="e.g. 3 office desks from warehouse to client site"
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="msb-page">
        <div className="msb-layout" style={{ padding: 48, textAlign: "center" }}>
          Loading route…
        </div>
      </div>
    );
  }

  return (
    <div className="msb-page">
      <header className="msb-hero">
        <div className="msb-hero-inner">
          <span className="msb-badge">Multi-stop route</span>
          <h1>Edit your booked route</h1>
          <p>
            Reorder intermediate stops, update site contacts, and adjust what moves on each driver leg.
            Your trip price stays the same - changes apply before a driver is assigned.
          </p>
          <a className="msb-hero-link" href="/dashboard">
            ← Back to dashboard
          </a>
        </div>
      </header>

      <div className="msb-layout">
        <div className="msb-main">
          {!editable && editableReason && (
            <div className="msb-card" style={{ marginBottom: 16, borderColor: "#ffc107" }}>
              <div className="msb-card-body">
                <strong>View only.</strong> {editableReason}
              </div>
            </div>
          )}

          {apierror && <div className="msb-error">{apierror}</div>}
          {successMsg && (
            <div
              className="msb-card"
              style={{ marginBottom: 16, borderColor: "#28a745", color: "#155724" }}
            >
              <div className="msb-card-body">{successMsg}</div>
            </div>
          )}

          <div className="msb-card">
            <div className="msb-card-header">
              <h2 className="msb-card-title">Route & driver legs</h2>
              <p className="msb-card-subtitle">
                Order ref {(parentOrderId || orderid || "").split("-")[0]}. Use Earlier / Later on
                intermediate stops to reorder.
              </p>
            </div>
            <div className="msb-card-body">
              {locations.length >= 3 ? (
                <div className="msb-route">
                  <div className="msb-route-step msb-route-step--pickup">
                    <div className="msb-route-marker">1</div>
                    {renderLocationFields(locations[0], 0)}
                  </div>

                  {Array.from({ length: legCount }, (_, legIndex) => (
                    <React.Fragment key={`leg-block-${legIndex}`}>
                      <div className="msb-route-connector msb-route-connector--leg" />
                      {renderLegCard(legIndex)}

                      {legIndex < locations.length - 2 ? (
                        <>
                          <div className="msb-route-connector" />
                          <div className="msb-route-step msb-route-step--waypoint">
                            <div className="msb-route-marker">{legIndex + 2}</div>
                            {renderLocationFields(locations[legIndex + 1], legIndex + 1)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="msb-route-connector" />
                          <div className="msb-route-step msb-route-step--delivery">
                            <div className="msb-route-marker">{locationCount}</div>
                            {renderLocationFields(
                              locations[locations.length - 1],
                              locations.length - 1
                            )}
                          </div>
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <p>This booking is not a multi-stop route.</p>
              )}
            </div>
          </div>
        </div>

        <aside className="msb-sidebar">
          <div className="msb-card msb-quote-panel">
            <div className="msb-quote-price">
              <div className="msb-quote-price-label">Booked price (unchanged)</div>
              <div className="msb-quote-price-value">
                <CurrencyFormat
                  value={(totalPrice || 0) / 100}
                  displayType="text"
                  thousandSeparator
                  prefix="R "
                  decimalScale={2}
                  fixedDecimalScale
                />
              </div>
            </div>

            <p className="msb-quote-meta">
              {locationCount} stops · {legCount} driver leg{legCount === 1 ? "" : "s"}
            </p>

            {editable ? (
              <button
                type="button"
                className="msb-btn msb-btn--primary msb-btn--block"
                disabled={saving}
                onClick={saveRoute}
              >
                {saving ? "Saving…" : "Save route changes"}
              </button>
            ) : (
              <a
                className="msb-btn msb-btn--secondary msb-btn--block"
                href={`${TRACK_BASE}?orderid=${encodeURIComponent(parentOrderId || orderid)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Track this route
              </a>
            )}

            <a
              className="msb-btn msb-btn--ghost msb-btn--block"
              href="/dashboard"
              style={{ marginTop: 8 }}
            >
              Back to dashboard
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}
